import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Empty } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { getWithdraws, WithdrawRequest } from '../firebase/firestore';
import { ledgerApi } from '../api/ledger';
import './Withdraws.css';

interface WithdrawalForecastItem {
  date: string;
  amountStars: number;
  count: number;
}

const Withdraws: React.FC = () => {
  const { firebaseUser } = useAuth();
  const { t } = useI18n();
  const [withdraws, setWithdraws] = useState<WithdrawRequest[]>([]);
  const [balance, setBalance] = useState<{
    total: number;
    availableStars: number;
    pendingStars: number;
    waitingStars: number;
  } | null>(null);
  const [forecast, setForecast] = useState<WithdrawalForecastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [forecastLoading, setForecastLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (firebaseUser) {
      loadWithdraws();
      loadForecast();
      loadBalance();
    }
  }, [firebaseUser]);

  const loadBalance = async () => {
    try {
      const data = await ledgerApi.getBalance();
      setBalance({
        total: data.total || 0,
        availableStars: data.availableStars || 0,
        pendingStars: data.pendingStars || 0,
        waitingStars: data.waitingStars || 0,
      });
    } catch (err) {
      console.error('Failed to load balance', err);
    }
  };

  const loadWithdraws = async () => {
    if (!firebaseUser) return;
    
    try {
      setLoading(true);
      const data = await getWithdraws(firebaseUser.uid);
      setWithdraws(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || t.app.withdraws.failedLoad);
    } finally {
      setLoading(false);
    }
  };

  const loadForecast = async () => {
    if (!firebaseUser) return;
    
    try {
      setForecastLoading(true);
      const data = await ledgerApi.getWithdrawalForecast();
      setForecast(data);
    } catch (err: any) {
      console.error('Failed to load withdrawal forecast:', err);
    } finally {
      setForecastLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'status-paid';
      case 'APPROVED':
        return 'status-approved';
      case 'PROCESSING':
        return 'status-processing';
      case 'REQUESTED':
        return 'status-requested';
      case 'REJECTED':
        return 'status-rejected';
      case 'FAILED':
        return 'status-failed';
      default:
        return '';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatForecastDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return <div className="withdraws-loading">{t.app.withdraws.loading}</div>;
  }

  if (error) {
    return <div className="withdraws-error">{t.app.common.error}: {error}</div>;
  }

  return (
    <div className="withdraws">
      <div className="withdraws-header">
        <h1>{t.app.withdraws.title}</h1>
        <Link to="/app/withdraws/new" className="new-withdraw-btn">
          {t.app.withdraws.newRequest}
        </Link>
      </div>

      {balance && (
        <Card className="balance-summary-card" style={{ marginBottom: '1.5rem' }}>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <div><small>{t.app.withdraws.totalLedger}</small><div><strong>{balance.total}</strong> {t.app.common.stars}</div></div>
            </Col>
            <Col xs={12} sm={6}>
              <div><small>{t.app.withdraws.available}</small><div><strong>{balance.availableStars}</strong> {t.app.common.stars}</div></div>
            </Col>
            <Col xs={12} sm={6}>
              <div><small>{t.app.withdraws.pendingWithdrawals}</small><div><strong>{balance.pendingStars}</strong> {t.app.common.stars}</div></div>
            </Col>
            <Col xs={12} sm={6}>
              <div><small>{t.app.withdraws.hold21days}</small><div><strong>{balance.waitingStars}</strong> {t.app.common.stars}</div></div>
            </Col>
          </Row>
          <p style={{ marginTop: '0.75rem', marginBottom: 0, opacity: 0.85 }}>
            {t.app.withdraws.payoutNote}
          </p>
        </Card>
      )}

      {/* Withdrawal Forecast Section */}
      <Card className="forecast-card" title={t.app.withdraws.forecastTitle} style={{ marginBottom: '2rem' }}>
        {forecastLoading ? (
          <div className="forecast-loading">{t.app.withdraws.loadingForecast}</div>
        ) : forecast.length === 0 ? (
          <Empty description={t.app.withdraws.noForecast} />
        ) : (
          <div className="forecast-list">
            {forecast.map((item, index) => (
              <div key={index} className="forecast-item">
                <div className="forecast-date">
                  <strong>{formatForecastDate(item.date)}</strong>
                  {item.count > 1 && (
                    <span className="forecast-count">{t.app.withdraws.paymentsCount.replace('{count}', String(item.count))}</span>
                  )}
                </div>
                <div className="forecast-amount">
                  <strong>{item.amountStars.toFixed(0)}</strong> {t.app.common.stars}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {withdraws.length === 0 ? (
        <div className="no-withdraws">
          <p>{t.app.withdraws.noRequests}</p>
          <Link to="/app/withdraws/new" className="create-first-btn">
            {t.app.withdraws.createFirst}
          </Link>
        </div>
      ) : (
        <div className="withdraws-list">
          {withdraws.map((withdraw) => (
            <div key={withdraw.id} className="withdraw-card">
              <div className="withdraw-header">
                <div className="withdraw-id">
                  <strong>#{withdraw.id.slice(0, 8)}</strong>
                  <span className={`status-badge ${getStatusColor(withdraw.status)}`}>
                    {withdraw.status}
                  </span>
                </div>
                <div className="withdraw-date">
                  {formatDate(withdraw.requestedAt)}
                </div>
              </div>

              <div className="withdraw-details">
                <div className="detail-item">
                  <span className="label">{t.app.withdraws.amount}</span>
                  <span className="value highlight">{withdraw.amountStars} {t.app.common.stars}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t.app.withdraws.destination}</span>
                  <span className="value address">{withdraw.destination.address}</span>
                </div>
                {withdraw.processedAt && (
                  <div className="detail-item">
                    <span className="label">{t.app.withdraws.processedOn}</span>
                    <span className="value">{formatDate(withdraw.processedAt)}</span>
                  </div>
                )}
                {withdraw.externalRef && (
                  <div className="detail-item">
                    <span className="label">{t.app.withdraws.externalRef}</span>
                    <span className="value">{withdraw.externalRef}</span>
                  </div>
                )}
                {withdraw.error && (
                  <div className="detail-item error">
                    <span className="label">{t.app.withdraws.errorLabel}</span>
                    <span className="value error-text">{withdraw.error}</span>
                  </div>
                )}
                {withdraw.rejectionReason && (
                  <div className="detail-item error">
                    <span className="label">{t.app.withdraws.rejectionReason}</span>
                    <span className="value error-text">{withdraw.rejectionReason}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Withdraws;
